/**
 * useStatsCache Hook
 *
 * Manages the stats cache with:
 * - Automatic initial fetching
 * - Dependency-aware fetch scheduling
 * - Automatic retry for failed entries
 * - Refresh/invalidation support
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  StatsCache,
  CacheKey,
  CacheEntry,
  createInitialCache,
  getReadyToFetch,
  getFailedEntries,
  shouldRetry,
  getRetryDelay,
  areDependenciesLoaded,
  fetchPrices,
  fetchChainTokenData,
  fetchGameAddresses,
  fetchGameCoreStats,
  fetchDailyStats,
  fetchSquareWins,
  fetchCacheEntry,
  fetchStakingData,
  fetchStreamData,
  PricesData,
  ChainTokenData,
  GameAddresses,
  GameCoreStats,
  DailyStatsData,
  SquareWinsData,
  StakingData,
  StreamData,
} from '@/services/statsCacheService'
import { BASE_CHAIN, BNB_CHAIN } from '@/lib/chains'
import { isPageVisible } from '@/lib/pageVisibility'
import { FEATURES } from '@/config/features'

// Retry check interval
const RETRY_CHECK_INTERVAL = 1000 // Check every second

export interface UseStatsCacheResult {
  cache: StatsCache
  isLoading: boolean
  failedCount: number
  refresh: () => void
}

export function useStatsCache(): UseStatsCacheResult {
  const [cache, setCache] = useState<StatsCache>(createInitialCache)
  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fetchingRef = useRef<Set<CacheKey>>(new Set())

  // Update a specific cache entry
  const updateEntry = useCallback(<K extends CacheKey>(key: K, entry: CacheEntry<StatsCache[K]['data']>) => {
    setCache(prev => ({
      ...prev,
      [key]: entry,
    }))
    // Remove from fetching set when done
    if (entry.status !== 'loading') {
      fetchingRef.current.delete(key)
    }
  }, [])

  // Fetch a specific cache entry
  const fetchEntry = useCallback(async (key: CacheKey, currentCache: StatsCache) => {
    // Skip if already fetching
    if (fetchingRef.current.has(key)) return
    fetchingRef.current.add(key)

    const entry = currentCache[key]

    switch (key) {
      case 'prices':
        await fetchCacheEntry(
          key,
          fetchPrices,
          entry as CacheEntry<PricesData>,
          (e) => updateEntry(key, e)
        )
        break

      case 'baseTokenData':
        await fetchCacheEntry(
          key,
          () => fetchChainTokenData(BASE_CHAIN.id),
          entry as CacheEntry<ChainTokenData>,
          (e) => updateEntry(key, e)
        )
        break

      case 'bnbTokenData':
        await fetchCacheEntry(
          key,
          () => fetchChainTokenData(BNB_CHAIN.id),
          entry as CacheEntry<ChainTokenData>,
          (e) => updateEntry(key, e)
        )
        break

      case 'gameAddresses':
        await fetchCacheEntry(
          key,
          fetchGameAddresses,
          entry as CacheEntry<GameAddresses>,
          (e) => updateEntry(key, e)
        )
        break

      case 'gameCoreStats': {
        const addresses = currentCache.gameAddresses.data
        if (!addresses) {
          console.warn('[useStatsCache] Cannot fetch gameCoreStats: gameAddresses not loaded')
          return
        }
        await fetchCacheEntry(
          key,
          () => fetchGameCoreStats(addresses),
          entry as CacheEntry<GameCoreStats>,
          (e) => updateEntry(key, e)
        )
        break
      }

      case 'dailyStats': {
        const addresses = currentCache.gameAddresses.data
        if (!addresses) {
          console.warn('[useStatsCache] Cannot fetch dailyStats: gameAddresses not loaded')
          return
        }
        await fetchCacheEntry(
          key,
          () => fetchDailyStats(addresses),
          entry as CacheEntry<DailyStatsData>,
          (e) => updateEntry(key, e)
        )
        break
      }

      case 'squareWins': {
        const addresses = currentCache.gameAddresses.data
        if (!addresses) {
          console.warn('[useStatsCache] Cannot fetch squareWins: gameAddresses not loaded')
          return
        }
        await fetchCacheEntry(
          key,
          () => fetchSquareWins(addresses),
          entry as CacheEntry<SquareWinsData>,
          (e) => updateEntry(key, e)
        )
        break
      }

      case 'stakingData':
        await fetchCacheEntry(
          key,
          fetchStakingData,
          entry as CacheEntry<StakingData>,
          (e) => updateEntry(key, e)
        )
        break

      case 'streamData':
        await fetchCacheEntry(
          key,
          fetchStreamData,
          entry as CacheEntry<StreamData>,
          (e) => updateEntry(key, e)
        )
        break
    }
  }, [updateEntry])

  // Process ready entries (fetch all that are ready)
  const processReadyEntries = useCallback((currentCache: StatsCache) => {
    const ready = getReadyToFetch(currentCache)

    // Prioritize failed entries (retry them first)
    const failed = ready.filter(k => currentCache[k].status === 'failed')
    const idle = ready.filter(k => currentCache[k].status === 'idle')

    // Fetch failed first, then idle
    const toFetch = [...failed, ...idle]

    for (const key of toFetch) {
      fetchEntry(key, currentCache)
    }
  }, [fetchEntry])

  // Initial fetch on mount
  useEffect(() => {
    processReadyEntries(cache)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Process dependent entries when dependencies become available
  useEffect(() => {
    // Game entries are only fetched when the Mining feature is enabled
    if (!FEATURES.mining) return
    // Check if gameAddresses just loaded and dependent entries need fetching
    if (cache.gameAddresses.status === 'loaded') {
      if (cache.gameCoreStats.status === 'idle') {
        fetchEntry('gameCoreStats', cache)
      }
      if (cache.dailyStats.status === 'idle') {
        fetchEntry('dailyStats', cache)
      }
      if (cache.squareWins.status === 'idle') {
        fetchEntry('squareWins', cache)
      }
    }
  }, [cache.gameAddresses.status, cache.gameCoreStats.status, cache.dailyStats.status, cache.squareWins.status, fetchEntry, cache])

  // Setup retry timer
  useEffect(() => {
    retryTimerRef.current = setInterval(() => {
      if (!isPageVisible()) return
      setCache(currentCache => {
        // Check for entries that should retry
        const failedEntries = getFailedEntries(currentCache)
        const readyToRetry = failedEntries.filter(key =>
          shouldRetry(currentCache[key]) && areDependenciesLoaded(currentCache, key)
        )

        if (readyToRetry.length > 0) {
          // Trigger fetches for ready-to-retry entries
          for (const key of readyToRetry) {
            fetchEntry(key, currentCache)
          }
        }

        return currentCache
      })
    }, RETRY_CHECK_INTERVAL)

    return () => {
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current)
      }
    }
  }, [fetchEntry])

  // Refresh function - invalidates cache and refetches
  const refresh = useCallback(() => {
    // Clear fetching set
    fetchingRef.current.clear()

    // Reset all entries to idle
    const freshCache = createInitialCache()
    setCache(freshCache)

    // Trigger initial fetches
    setTimeout(() => {
      processReadyEntries(freshCache)
    }, 0)
  }, [processReadyEntries])

  // Calculate derived state
  const isLoading = Object.values(cache).some(entry => entry.status === 'loading')
  const failedCount = Object.values(cache).filter(entry => entry.status === 'failed').length

  return {
    cache,
    isLoading,
    failedCount,
    refresh,
  }
}

// Helper to check if all required entries are loaded for a section
export function isSectionLoaded(cache: StatsCache, section: 'tokenOverview' | 'supply' | 'game' | 'daily' | 'squareWins' | 'staking' | 'stream'): boolean {
  switch (section) {
    case 'tokenOverview':
      return cache.prices.status === 'loaded' && cache.gameCoreStats.status === 'loaded'
    case 'supply':
      return cache.baseTokenData.status === 'loaded' && cache.bnbTokenData.status === 'loaded'
    case 'game':
      return cache.gameCoreStats.status === 'loaded'
    case 'daily':
      return cache.dailyStats.status === 'loaded'
    case 'squareWins':
      return cache.squareWins.status === 'loaded'
    case 'staking':
      return cache.stakingData.status === 'loaded'
    case 'stream':
      return cache.streamData.status === 'loaded'
    default:
      return false
  }
}

// Helper to check if section is loading
export function isSectionLoading(cache: StatsCache, section: 'tokenOverview' | 'supply' | 'game' | 'daily' | 'squareWins' | 'staking' | 'stream'): boolean {
  switch (section) {
    case 'tokenOverview':
      return cache.prices.status === 'loading' || cache.gameCoreStats.status === 'loading'
    case 'supply':
      return cache.baseTokenData.status === 'loading' || cache.bnbTokenData.status === 'loading'
    case 'game':
      return cache.gameCoreStats.status === 'loading'
    case 'daily':
      return cache.dailyStats.status === 'loading'
    case 'squareWins':
      return cache.squareWins.status === 'loading'
    case 'staking':
      return cache.stakingData.status === 'loading'
    case 'stream':
      return cache.streamData.status === 'loading'
    default:
      return false
  }
}

// Helper to check if section has any failures
export function hasSectionFailed(cache: StatsCache, section: 'tokenOverview' | 'supply' | 'game' | 'daily' | 'squareWins' | 'staking' | 'stream'): boolean {
  switch (section) {
    case 'tokenOverview':
      return cache.prices.status === 'failed' || cache.gameCoreStats.status === 'failed'
    case 'supply':
      return cache.baseTokenData.status === 'failed' || cache.bnbTokenData.status === 'failed'
    case 'game':
      return cache.gameCoreStats.status === 'failed'
    case 'daily':
      return cache.dailyStats.status === 'failed'
    case 'squareWins':
      return cache.squareWins.status === 'failed'
    case 'staking':
      return cache.stakingData.status === 'failed'
    case 'stream':
      return cache.streamData.status === 'failed'
    default:
      return false
  }
}
